import type { ReactNode } from 'react';
import { errorMessage, isApiError } from '../api/client';
import { Button } from './Button';

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** Error panel with the server's request id so support can trace the failure. */
export function ErrorState({ error, onRetry, title = 'Something went wrong' }: { error: unknown; onRetry?: () => void; title?: string }) {
  const forbidden = isApiError(error) && (error.code === 'FORBIDDEN' || error.status === 403);
  return (
    <div className="notice notice-danger" role="alert">
      <strong>{forbidden ? 'Access denied' : title}</strong>
      <p style={{ margin: '4px 0 0' }}>{errorMessage(error)}</p>
      {isApiError(error) && error.requestId && (
        <p className="small" style={{ margin: '4px 0 0' }}>
          Reference: <code>{error.requestId}</code>
        </p>
      )}
      {onRetry && !forbidden && (
        <div style={{ marginTop: 8 }}>
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <span role="status" className="row muted">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

export function PageLoading() {
  return (
    <div className="full-page-center">
      <Spinner />
    </div>
  );
}

/** Explicit marker for an action whose API does not exist yet. */
export function NotYetAvailable({ children = 'Not yet available' }: { children?: ReactNode }) {
  return <span className="badge" title="This feature has no API yet">{children}</span>;
}
